import style from './tree.module.css';
import { NodeDropTargetLocation, TreeNodeData, XY } from '../model';
import { useExpansionSet } from '../contexts/NodeExpansionSet';
import { useCallback, useMemo, useReducer, useRef, useState } from 'react';
import { calculateNodeStatistics } from '../pathing';
import { TreeNode } from './TreeNode';
import { useTreeContext } from '../contexts/TreeContext';

function getDropTarget(mouse: XY, source_id: number) {
  const els = document.elementsFromPoint(mouse.x, mouse.y);
  let tree_node = els.find((a) => a.classList.contains(style.node));

  if (!tree_node) {
    console.error('No tree node');
    return null;
  }

  const path = tree_node.getAttribute('data-path');

  if (!path) {
    console.error('Missing path');
    return null;
  }

  const path_list = path.split(',').map(Number);
  const path_vals = new Set(path_list);

  if (path_vals.has(source_id)) {
    return null;
  }

  const rect = tree_node.getBoundingClientRect();

  let section: NodeDropTargetLocation = (() => {
    const split = rect.height / 3;
    if (mouse.y < rect.top + split) {
      return 'before';
    } else if (mouse.y < rect.top + split * 2) {
      return 'inside';
    }

    return 'after';
  })();

  if (section === 'after') {
    const next_element = tree_node.nextSibling;

    if (next_element instanceof HTMLElement) {
      const current_depth = Number(tree_node.getAttribute('data-depth'));
      const next_depth = Number(next_element.getAttribute('data-depth'));

      if (next_depth > current_depth) {
        tree_node = next_element;
        section = 'before' as const;
      }
    }
  }

  return {
    path: path_list,
    target_element: tree_node,
    target_id: Number(tree_node.getAttribute('data-id')),
    location_on_target: section,
  };
}

export function Tree() {
  const { expansionSet, expand } = useExpansionSet();
  const { tree_data, move: moveNodeInTree } = useTreeContext();
  const dragFrame = useRef<number | null>(null);
  const dragNode = useRef<{
    element: HTMLElement;
    node: TreeNodeData;
  } | null>(null);
  const { node_stats } = useMemo(
    () => calculateNodeStatistics(tree_data.children, expansionSet),
    [tree_data, expansionSet],
  );
  const [is_dragging, setIsDragging] = useState(false);
  const [dropState, setDropState] = useState<{
    node_id: number;
    location: NodeDropTargetLocation;
  } | null>(null);

  const [editNode, setEditNode] = useState<number | null>(null);

  const startDragHandler = useCallback(() => {
    let last_hovered_node = null as Element | null;
    let last_insert_pos = null as NodeDropTargetLocation | null;

    function pointermove(e: PointerEvent) {
      if (dragFrame.current) {
        window.cancelAnimationFrame(dragFrame.current);
      }

      dragFrame.current = window.requestAnimationFrame(() => {
        const currentDragNode = dragNode.current;
        if (!currentDragNode) {
          throw new Error('Missing drag item');
        }

        const drop_target = getDropTarget(
          { x: e.clientX, y: e.clientY },
          currentDragNode.node.id,
        );

        if (!drop_target) {
          return;
        }

        const { location_on_target, target_id, target_element } = drop_target;

        if (
          location_on_target === last_insert_pos &&
          last_hovered_node === target_element
        ) {
          return;
        }

        last_hovered_node = target_element;
        last_insert_pos = location_on_target;

        setDropState({
          node_id: target_id,
          location: location_on_target,
        });
      });
    }

    window.addEventListener('pointermove', pointermove);

    return () => {
      if (dragFrame.current) {
        window.cancelAnimationFrame(dragFrame.current);
      }
      window.removeEventListener('pointermove', pointermove);
    };
  }, []);

  const onDragStart = useCallback(
    (ref: HTMLElement, node: TreeNodeData) => {
      setIsDragging(true);

      dragNode.current = {
        element: ref,
        node,
      };

      document.body.classList.add('dragging');
      window.getSelection()?.empty();

      const stopDragHandler = startDragHandler();

      function onStopDragging(ev: PointerEvent) {
        const source_id = dragNode.current?.node.id;

        (function handleDrop() {
          if (!source_id) {
            return;
          }

          const drop_target = getDropTarget(
            { x: ev.clientX, y: ev.clientY },
            source_id,
          );

          if (!drop_target) {
            return;
          }

          moveNodeInTree(
            source_id,
            drop_target.target_id,
            drop_target.location_on_target,
            drop_target.path,
          );

          if (drop_target.location_on_target === 'inside') {
            // Open up the drop target if it's dropped inside
            expand(drop_target.target_id);
          }
        })();

        dragNode.current = null;
        document.body.classList.remove('dragging');
        stopDragHandler();
        setIsDragging(false);
        window.removeEventListener('pointerup', onStopDragging);
        setDropState(null);
      }

      window.addEventListener('pointerup', onStopDragging);
    },
    [moveNodeInTree, expand],
  );

  const onClickCapture = useCallback(
    (ev: React.PointerEvent) => {
      if (!editNode) {
        return;
      }

      if (ev.target instanceof HTMLInputElement) {
        if (ev.target.dataset.nodeid === editNode.toString()) {
          return;
        }
      }

      setEditNode(null);
    },
    [editNode],
  );

  const onKeyUpCapture = useCallback(
    (ev: React.KeyboardEvent) => {
      if (!editNode) {
        return;
      }

      if (ev.key === 'Escape') {
        setEditNode(null);
      }
    },
    [editNode],
  );

  return (
    <div
      className={style.container}
      onPointerUpCapture={onClickCapture}
      onKeyUpCapture={onKeyUpCapture}
    >
      <div
        data-id="root"
        className={`${style.tree} ${is_dragging ? 'dragging' : ''}`}
      >
        {tree_data.children.map((a, x) => (
          <TreeNode
            editNode={editNode}
            onSetEditNode={setEditNode}
            isDragging={is_dragging}
            stats={node_stats}
            key={a.id}
            node={a}
            path={a.id.toString()}
            onDragStart={onDragStart}
            currentTarget={dropState}
            isFinalChild={x === tree_data.children.length - 1}
          />
        ))}
      </div>
    </div>
  );
}
